import type { TodaySignalSeverity } from "@/lib/dashboard/today-signal-types";

export const SIGNALS_SETTINGS_GROUP = "SIGNALS";

export const SIGNALS_ADMIN_CONFIG_KEY = "SIGNALS_ADMIN_CONFIG";
export const SIGNALS_STAFF_CONFIG_KEY = "SIGNALS_STAFF_CONFIG";
export const SIGNALS_TEACHER_CONFIG_KEY = "SIGNALS_TEACHER_CONFIG";
export const SIGNALS_MEMBER_CONFIG_KEY = "SIGNALS_MEMBER_CONFIG";

export type TodaySignalRole = "admin" | "staff" | "teacher" | "member";

export type SignalCatalogEntry = {
  id: string;
  label: string;
  description: string;
  severity: TodaySignalSeverity;
  /** When false, shown in settings but cannot be enabled (permission floor). */
  configurable: boolean;
  lockReason?: string;
};

export type RoleSignalConfigItem = {
  id: string;
  enabled: boolean;
};

export type StudioSignalsSettings = Record<
  TodaySignalRole,
  RoleSignalConfigItem[]
>;

export const NON_CONFIGURABLE_SIGNAL_IDS = new Set(["all-clear", "welcome"]);

/** Staff Today's Signal ids staff must not see - permission floor. */
export const STAFF_FORBIDDEN_SIGNAL_IDS = ["pending-revenue"] as const;

/** Admin daily workflow — “Also today” rows (enrollment steps are always on). */
const ADMIN_SIGNAL_CATALOG: SignalCatalogEntry[] = [
  {
    id: "my-tasks-open",
    label: "My tasks",
    description: "Open tasks assigned to you on the daily workflow.",
    severity: "action",
    configurable: true,
  },
  {
    id: "time-clock-pending",
    label: "Time clock approvals",
    description: "Time entries waiting for approval.",
    severity: "action",
    configurable: true,
  },
  {
    id: "pending-makeup-requests",
    label: "Makeup requests",
    description: "Requests waiting for review.",
    severity: "action",
    configurable: true,
  },
  {
    id: "class-starting-soon",
    label: "Class starting soon",
    description: "Shown when a class starts within the hour.",
    severity: "urgent",
    configurable: true,
  },
];

/** Staff daily workflow — “Also today” rows (enrollment steps are always on). */
const STAFF_SIGNAL_CATALOG: SignalCatalogEntry[] = [
  {
    id: "my-tasks-open",
    label: "My tasks",
    description: "Open tasks assigned to you on the daily workflow.",
    severity: "action",
    configurable: true,
  },
  {
    id: "pending-makeup-requests",
    label: "Makeup requests",
    description: "Requests waiting for review.",
    severity: "action",
    configurable: true,
  },
  {
    id: "class-starting-soon",
    label: "Class starting soon",
    description: "Shown when a class starts within the hour.",
    severity: "urgent",
    configurable: true,
  },
];

const TEACHER_SIGNAL_CATALOG: SignalCatalogEntry[] = [
  {
    id: "private-lessons-today",
    label: "Private lessons today",
    description: "One-on-one sessions scheduled for today.",
    severity: "action",
    configurable: true,
  },
  {
    id: "class-starting-soon",
    label: "Class starting soon",
    description: "A class you teach starts within the hour.",
    severity: "urgent",
    configurable: true,
  },
  {
    id: "new-enrollments",
    label: "New enrollments",
    description: "New students enrolled in your classes this week.",
    severity: "action",
    configurable: true,
  },
  {
    id: "low-enrollment",
    label: "Low enrollment",
    description: "Your classes below the studio enrollment threshold.",
    severity: "info",
    configurable: true,
  },
  {
    id: "private-lessons-upcoming",
    label: "Upcoming private lessons",
    description: "Private sessions in the next seven days.",
    severity: "info",
    configurable: true,
  },
  {
    id: "private-lessons-only",
    label: "Private lessons only",
    description: "Shown when a teacher has no group classes assigned.",
    severity: "info",
    configurable: true,
  },
];

const MEMBER_SIGNAL_CATALOG: SignalCatalogEntry[] = [
  {
    id: "balance-due",
    label: "Balance due",
    description: "Outstanding balance on the family account.",
    severity: "action",
    configurable: true,
  },
  {
    id: "makeup-eligible",
    label: "Eligible makeups",
    description: "Missed classes that can request a makeup.",
    severity: "action",
    configurable: true,
  },
  {
    id: "makeup-change-requested",
    label: "Makeup response needed",
    description: "Studio-proposed makeup times awaiting parent response.",
    severity: "action",
    configurable: true,
  },
  {
    id: "pending-enrollments",
    label: "Pending enrollments",
    description: "Class registrations still awaiting confirmation.",
    severity: "action",
    configurable: true,
  },
  {
    id: "class-starting-soon",
    label: "Class starting soon",
    description: "A enrolled class starts within the hour.",
    severity: "urgent",
    configurable: true,
  },
  {
    id: "waitlist",
    label: "Waitlist",
    description: "Classes the family is waiting to join.",
    severity: "info",
    configurable: true,
  },
];

export const SIGNAL_CATALOG_BY_ROLE: Record<
  TodaySignalRole,
  SignalCatalogEntry[]
> = {
  admin: ADMIN_SIGNAL_CATALOG,
  staff: STAFF_SIGNAL_CATALOG,
  teacher: TEACHER_SIGNAL_CATALOG,
  member: MEMBER_SIGNAL_CATALOG,
};

export const SIGNALS_ROLE_LABEL: Record<TodaySignalRole, string> = {
  admin: "Admin",
  staff: "Staff",
  teacher: "Teacher",
  member: "Member",
};

function defaultEnabledForRole(
  role: TodaySignalRole,
  signalId: string,
): boolean {
  if (role === "staff") {
    return !STAFF_FORBIDDEN_SIGNAL_IDS.includes(
      signalId as (typeof STAFF_FORBIDDEN_SIGNAL_IDS)[number],
    );
  }
  return true;
}

export function buildDefaultRoleSignalConfig(
  role: TodaySignalRole,
): RoleSignalConfigItem[] {
  return SIGNAL_CATALOG_BY_ROLE[role].map((entry) => ({
    id: entry.id,
    enabled: defaultEnabledForRole(role, entry.id),
  }));
}

export function buildDefaultStudioSignalsSettings(): StudioSignalsSettings {
  return {
    admin: buildDefaultRoleSignalConfig("admin"),
    staff: buildDefaultRoleSignalConfig("staff"),
    teacher: buildDefaultRoleSignalConfig("teacher"),
    member: buildDefaultRoleSignalConfig("member"),
  };
}

export function getSignalCatalogEntry(
  role: TodaySignalRole,
  signalId: string,
): SignalCatalogEntry | undefined {
  return SIGNAL_CATALOG_BY_ROLE[role].find((entry) => entry.id === signalId);
}
